import {
  BIRD,
  CAT,
  CAT_PATTERNS,
  DOG_FLOPPY,
  DOG_FLOPPY_PATTERNS,
  DOG_PRICK,
  DOG_PRICK_PATTERNS,
  EYE_COLOURS,
  FAMILY_TONES,
  FERRET,
  type Family,
  GHOST_DARK,
  GHOST_LIGHT,
  GUINEA_PIG,
  GUINEA_PIG_PATTERNS,
  LIZARD,
  PAW,
  type PatternMask,
  type PixelMap,
  RABBIT,
  RABBIT_PATTERNS,
  TONES,
  type Tone,
  WHITE_MARKING,
  WING,
  WING_FILL,
  WING_OUTLINE,
  WING_TOP,
} from "./spriteArt";

export type SpriteSpecies =
  | "cat"
  | "dog"
  | "rabbit"
  | "guinea_pig"
  | "ferret"
  | "bird"
  | "lizard";
export type SpriteEars = "floppy" | "prick";
export type SpriteSwatch =
  | "black"
  | "white"
  | "cream"
  | "grey"
  | "silver"
  | "lilac"
  | "chocolate"
  | "brown"
  | "red"
  | "orange"
  | "golden"
  | "tan"
  | "fawn"
  | "sable"
  | "green"
  | "blue"
  | "yellow";
export type SpritePattern =
  | "tabby"
  | "bicolour"
  | "calico"
  | "tortoiseshell"
  | "tricolour"
  | "merle"
  | "brindle"
  | "point"
  | "tan_points"
  | "spotted";
export type SpriteEyes = "amber" | "brown" | "green" | "blue" | "odd";

/** Mirrors brian `modules/sprite/traits.py` `SpriteOut`; change both together. */
export interface SpriteTraits {
  species: SpriteSpecies | null;
  ears: SpriteEars | null;
  coat: SpriteSwatch | null;
  pattern: SpritePattern | null;
  patches: readonly SpriteSwatch[];
  eyes: SpriteEyes | null;
  deceased: boolean;
}

const OUTLINE = "currentColor";

interface Silhouette {
  map: PixelMap;
  family: Family;
  patterns: Partial<Record<SpritePattern, PatternMask>>;
  fixed?: Readonly<Record<string, string>>;
}

const SKIN = "#C9A08A";

const SILHOUETTES: Record<SpriteSpecies | "dog_prick", Silhouette> = {
  cat: { map: CAT, family: "cat", patterns: CAT_PATTERNS },
  dog: { map: DOG_FLOPPY, family: "dog", patterns: DOG_FLOPPY_PATTERNS },
  dog_prick: { map: DOG_PRICK, family: "dog", patterns: DOG_PRICK_PATTERNS },
  rabbit: { map: RABBIT, family: "rabbit", patterns: RABBIT_PATTERNS },
  guinea_pig: {
    map: GUINEA_PIG,
    family: "small",
    patterns: GUINEA_PIG_PATTERNS,
    fixed: { K: SKIN },
  },
  ferret: { map: FERRET, family: "small", patterns: {}, fixed: { K: SKIN } },
  bird: {
    map: BIRD,
    family: "bird",
    patterns: {},
    fixed: { Y: "#E8C66A", Q: "#5E86C8" },
  },
  lizard: { map: LIZARD, family: "lizard", patterns: {} },
};

/** A silhouette without a pattern's mask draws its nearest, else the solid coat. */
const PATTERN_FALLBACK: Partial<Record<SpritePattern, SpritePattern>> = {
  tricolour: "bicolour",
  calico: "tortoiseshell",
};

const PATTERNED_SLOTS = new Set(["C", "M", "S"]);

const tone = (family: Family, swatch: SpriteSwatch): Tone => ({
  ...TONES[swatch],
  ...FAMILY_TONES[family]?.[swatch],
});

const DARK_COATS: Partial<Record<Family, ReadonlySet<SpriteSwatch>>> = {
  cat: new Set(["black"]),
  dog: new Set(["black", "chocolate"]),
};

/** Unrecorded eyes: lighter on dark coats so they read, else a closed line. */
function defaultEyes(
  family: Family,
  coat: SpriteSwatch,
  pattern: SpritePattern | null,
): string {
  if (!DARK_COATS[family]?.has(coat)) return OUTLINE;
  if (family === "cat") {
    return pattern === null ? EYE_COLOURS.green : EYE_COLOURS.amber;
  }
  return EYE_COLOURS.brown;
}

function eyeColour(eyes: SpriteEyes, x: number): string {
  if (eyes === "odd") return x < 8 ? EYE_COLOURS.blue : EYE_COLOURS.amber;
  return EYE_COLOURS[eyes];
}

type Grid = (string | null)[][];

const blank = (width: number): Grid =>
  Array.from({ length: 16 }, () => Array<string | null>(width).fill(null));

const checker = (x: number, y: number): string =>
  ((x >> 1) + (y >> 1)) % 2 === 0 ? GHOST_LIGHT : GHOST_DARK;

function ghostGrid(map: PixelMap): Grid {
  const grid = blank(16);
  map.forEach((row, y) =>
    [...row].forEach((slot, x) => {
      if (slot === ".") return;
      grid[y][x] = slot === "O" || slot === "P" ? OUTLINE : checker(x, y);
    }),
  );
  return grid;
}

function fullGrid(
  silhouette: Silhouette,
  traits: SpriteTraits,
  coat: SpriteSwatch,
): Grid {
  const { family, map, fixed } = silhouette;
  const base = tone(family, coat);
  const pattern = traits.pattern;
  const mask = pattern
    ? (silhouette.patterns[pattern] ??
      silhouette.patterns[PATTERN_FALLBACK[pattern] ?? pattern])
    : undefined;
  const patch = (index: number): string => {
    const swatch = traits.patches[index];
    if (!swatch) return base.shade;
    const patchTone = tone(family, swatch);
    return patchTone.patch ?? patchTone.coat;
  };
  const marks: Record<string, () => string> = {
    S: () => base.shade,
    W: () => WHITE_MARKING,
    A: () => patch(0),
    B: () => patch(1),
  };
  const eye = (x: number): string =>
    traits.eyes
      ? eyeColour(traits.eyes, x)
      : defaultEyes(family, coat, pattern);
  const slots: Record<string, (x: number) => string> = {
    O: () => OUTLINE,
    C: () => base.coat,
    S: () => base.shade,
    M: () => base.light,
    R: () => (family === "dog" ? base.dark : base.nose),
    N: () => base.nose,
    P: eye,
  };

  const grid = blank(16);
  map.forEach((row, y) =>
    [...row].forEach((slot, x) => {
      if (slot === ".") return;
      const mark = mask?.[y]?.[x];
      if (mark && mark !== "." && PATTERNED_SLOTS.has(slot)) {
        grid[y][x] = marks[mark]();
        return;
      }
      grid[y][x] = slots[slot]?.(x) ?? fixed?.[slot] ?? OUTLINE;
    }),
  );
  return grid;
}

/** Slides the left wing in from outside the box until it first overlaps the body. */
function wingOffset(body: Grid): number {
  for (let offset = -WING[0].length; offset < 16; offset++) {
    const touches = WING.some((row, r) =>
      [...row].some(
        (cell, c) =>
          cell !== "." &&
          offset + c >= 0 &&
          offset + c < 16 &&
          body[WING_TOP + r][offset + c] !== null,
      ),
    );
    if (touches) return offset;
  }
  return 0;
}

/** Wing pixels the body doesn't cover, in viewBox units from x = -7. */
function wingGrid(body: Grid): Grid {
  const pad = WING[0].length;
  const grid = blank(16 + pad * 2);
  const left = wingOffset(body);
  const mirrored = body.map((row) => [...row].reverse());
  const right = wingOffset(mirrored);
  WING.forEach((row, r) =>
    [...row].forEach((cell, c) => {
      if (cell === ".") return;
      const fill = cell === "d" ? WING_OUTLINE : WING_FILL;
      const y = WING_TOP + r;
      const lx = left + c;
      if (lx < 0 || body[y][lx] === null) grid[y][lx + pad] = fill;
      const rx = 15 - (right + c);
      if (rx > 15 || body[y][rx] === null) grid[y][rx + pad] = fill;
    }),
  );
  return grid;
}

interface SpritePath {
  fill: string;
  d: string;
}

/** One path per colour, each run of a row as a 1px-tall rectangle. */
function toPaths(grid: Grid, originX = 0): SpritePath[] {
  const runs = new Map<string, string[]>();
  grid.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const fill = row[x];
      if (fill === null) {
        x++;
        continue;
      }
      let w = 1;
      while (row[x + w] === fill) w++;
      const list = runs.get(fill) ?? [];
      list.push(`M${x + originX} ${y}h${w}v1h-${w}z`);
      runs.set(fill, list);
      x += w;
    }
  });
  return [...runs.entries()].map(([fill, segments]) => ({
    fill,
    d: segments.join(""),
  }));
}

interface SpriteDrawing {
  /** `species-coat` or `species-ghost`, `paw` for an unknown species. */
  name: string;
  body: SpritePath[];
  /** Drawn outside the 16px box, behind a faded body. */
  wings: SpritePath[] | null;
}

export function drawSprite(
  traits: SpriteTraits | null | undefined,
): SpriteDrawing {
  const species = traits?.species ?? null;
  const silhouette =
    species === null
      ? null
      : SILHOUETTES[
          species === "dog" && traits?.ears === "prick" ? "dog_prick" : species
        ];
  const coat = traits?.coat ?? null;
  let body: Grid;
  let name: string;
  if (silhouette === null || traits == null) {
    body = ghostGrid(PAW);
    name = "paw";
  } else if (coat === null) {
    body = ghostGrid(silhouette.map);
    name = `${species}-ghost`;
  } else {
    body = fullGrid(silhouette, traits, coat);
    name = `${species}-${coat}${traits.pattern ? `-${traits.pattern}` : ""}`;
  }
  return {
    name,
    body: toPaths(body),
    wings: traits?.deceased ? toPaths(wingGrid(body), -WING[0].length) : null,
  };
}
